import React from 'react';
import {
  Column,
  Copy,
  Flex,
  Heading,
  Row,
  Stack,
} from '@grubhubprod/gh-cookbook-react/dist';
import { formatPenniesToCurrency } from 'src/common/utils/formatPenniesToCurrency';
import { PageSection } from 'src/components/selfService/layout/PageSection/PageSection';
import { PageSectionTitle } from 'src/components/selfService/layout/PageSectionTitle/PageSectionTitle';
import { BalancePortalButton } from 'src/components/selfService/common/Balance/components/BalancePortalButton/BalancePortalButton';
import { PoweredByBalance } from 'src/components/selfService/common/Balance/components/PoweredByBalance/PoweredByBalance';
import { useBalanceContext } from 'src/components/selfService/common/Balance/hooks/useBalanceContext';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import { BalanceInvoicesSectionLoading } from 'src/components/Account/BalanceInvoicesSection/BalanceInvoicesSectionLoading';
import {
  getInvoicesPortalPage,
  portalButtonText,
  withoutNetTermsCopy,
  withNetTermsCopy,
} from 'src/components/Account/BalanceInvoicesSection/BalanceInvoicesSection.utils';

export function BalanceInvoicesSection(): JSX.Element {
  const { isDesktop } = useScreenSizes();
  const balanceContext = useBalanceContext();

  const isOnNetTerms = balanceContext.isOnNetTerms;
  const creditLimit = balanceContext.creditInfo?.creditLimit ?? null;
  const sectionCopy = isOnNetTerms ? withNetTermsCopy : withoutNetTermsCopy;
  const invoicesPortalPage = getInvoicesPortalPage(isOnNetTerms);

  const shouldRemoveBottomPadding = !isOnNetTerms && isDesktop;

  if (balanceContext.isCreditInfoLoading) {
    return <BalanceInvoicesSectionLoading />;
  }

  return (
    <PageSection dataTestId="balance-invoices-section">
      <Row>
        <Column tablet={12} desktop={9}>
          <PageSectionTitle
            title={sectionCopy.title}
            subtitle={sectionCopy.description}
            headingVariant="h4"
            y={shouldRemoveBottomPadding ? 'none' : 'md'}
          />

          {isOnNetTerms && (
            <Stack y={isDesktop ? 'xxx-sm' : 'lg'}>
              <Stack y="xxx-sm">
                <Heading variant="h5" color="secondaryText">
                  {withNetTermsCopy.invoices.title}
                </Heading>
              </Stack>
              <Stack y="sm">
                <Copy variant="body">
                  {withNetTermsCopy.invoices.description}
                </Copy>
              </Stack>
              <Stack y="xxx-sm">
                <Heading variant="h5" color="secondaryText">
                  {withNetTermsCopy.creditLimit.title}
                </Heading>
              </Stack>
              <Copy variant="body">{formatPenniesToCurrency(creditLimit)}</Copy>
            </Stack>
          )}
        </Column>

        <Column tablet={12} desktop={3}>
          <Flex
            alignItems={isDesktop ? 'flex-end' : 'flex-start'}
            justifyContent={isDesktop ? 'space-between' : 'flex-start'}
            flexDirection="column"
            height="100%"
          >
            <Stack
              y={!shouldRemoveBottomPadding ? 'sm' : 'none'}
              alignSelf={isDesktop ? 'flex-end' : 'stretch'}
            >
              <BalancePortalButton
                balancePortalPage={invoicesPortalPage}
                text={portalButtonText}
                block={!isDesktop}
              />
            </Stack>
            {!shouldRemoveBottomPadding && <PoweredByBalance />}
          </Flex>
        </Column>
      </Row>
    </PageSection>
  );
}
